// MARK://dhk8/pos/pages/PendingOrdersPage
// "Órdenes por aceptar" (/orders/pending) — BANDEJA DE ACEPTACIÓN MANUAL.
//
// Super-set guest order-accept (Fase 1.6). El comensal (QR) arma su pedido y NO
// va solo a cocina: la orden nace PENDING_ACCEPTANCE y espera que el staff la
// autorice aquí. Aceptar → OPEN + ventana de gracia (fireHoldUntil) para revisar
// antes de comandar; Rechazar (razón obligatoria) → REJECTED y libera la mesa.
//
// El GATE de método de pago es del backend: si el negocio no configuró métodos
// o el elegido no está habilitado, `acceptOrder` falla con error VISIBLE (no
// best-effort) — lo mostramos en la card.
//
// Polling cada 8 s (no hay gateway WS todavía — eso es Fase 2). Estructura y
// tokens calcados de TakeoutOrdersPage (reglas NintaiGO: sin radius/sombra).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Inbox, RefreshCw, Check, Clock, X, CreditCard, Banknote, Globe, AlertTriangle } from 'lucide-react'

import {
  listPendingAcceptanceOrders,
  listOrdersWithPendingRounds,
  acceptRound,
  rejectRound,
  acceptOrder,
  fireOrder,
  rejectOrder,
  listOrders,
  getTenantPaymentMethods,
  type BackendOrderDto,
} from '../lib/clients/orders.js'
import { getTenantId } from '../lib/api.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { useAuthStore } from '../stores/auth-store.js'
import { formatCents } from '../lib/money.js'

const POLL_MS = 8_000
const GUEST_SENTINEL = '00000000-0000-0000-0000-000000000001'

function elapsedMin(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000))
}
function fmtElapsed(iso: string): string {
  const mins = elapsedMin(iso)
  if (mins < 1) return 'ahora'
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}
/** Identificador legible del pedido: código de recolección (XXXX-99999) si es
 *  para llevar, si no el folio (ORD-000123). ANTES se pintaba `id.slice(0,8)` —
 *  un "folio" inventado en el cliente que, siendo el id un UUID v7 (ordenado por
 *  tiempo), salía casi idéntico en todos los pedidos del día. Mismo patrón que
 *  TakeoutOrdersPage. */
function orderRef(o: BackendOrderDto): string {
  return o.pickupCode ?? o.folio
}
function waitTier(mins: number): 'fresh' | 'wait' | 'long' {
  if (mins < 2) return 'fresh'
  if (mins < 6) return 'wait'
  return 'long'
}

/** Estatus del pedido → etiqueta legible + clase de color (vista Historial). */
function statusMeta(s: BackendOrderDto['status']): { label: string; cls: string } {
  switch (s) {
    case 'PENDING_ACCEPTANCE': return { label: 'Por aceptar', cls: 'wait' }
    case 'OPEN': return { label: 'Abierta', cls: 'open' }
    case 'SENT_TO_KITCHEN': return { label: 'En cocina', cls: 'open' }
    case 'PARTIALLY_PAID': return { label: 'Pago parcial', cls: 'wait' }
    case 'CLOSED': return { label: 'Cerrada', cls: 'done' }
    case 'CANCELLED': return { label: 'Cancelada', cls: 'muted' }
    case 'REJECTED': return { label: 'Rechazada', cls: 'danger' }
    default: return { label: s, cls: 'muted' }
  }
}

/** Tipo de pedido → etiqueta corta para la vista Historial. */
function typeLabel(t: BackendOrderDto['type']): string {
  switch (t) {
    case 'DINE_IN': return 'Mesa'
    case 'TAKEOUT': return 'Para llevar'
    case 'DELIVERY': return 'Domicilio'
    case 'DRIVE_THROUGH': return 'Auto'
    default: return t
  }
}

/** Método de pago elegido por el comensal → etiqueta + ícono para la card. */
function methodMeta(method: string | null | undefined): { label: string; Icon: typeof CreditCard; known: boolean } {
  switch (method) {
    case 'CASH': return { label: 'Efectivo', Icon: Banknote, known: true }
    case 'TERMINAL': return { label: 'Terminal', Icon: CreditCard, known: true }
    case 'ONLINE': return { label: 'En línea', Icon: Globe, known: true }
    default: return { label: 'Sin método', Icon: AlertTriangle, known: false }
  }
}

export default function PendingOrdersPage() {
  const tenantId = useTenantId()
  const user = useAuthStore((s) => s.user)
  const [orders, setOrders] = useState<BackendOrderDto[]>([])
  // Cuentas ya abiertas con una RONDA que el comensal sometió y espera visto
  // bueno del mesero ("Ronda N por aceptar"). Separado de `orders` (que son
  // órdenes PENDING_ACCEPTANCE completas).
  const [roundOrders, setRoundOrders] = useState<BackendOrderDto[]>([])
  const [roundRejectingId, setRoundRejectingId] = useState<string | null>(null)
  const [roundRejectReason, setRoundRejectReason] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [cardError, setCardError] = useState<Record<string, string>>({})
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [acceptedCount, setAcceptedCount] = useState(0)
  // Vista: bandeja de aceptación (default) vs historial de pedidos + su estatus.
  const [view, setView] = useState<'pending' | 'history'>('pending')
  const [history, setHistory] = useState<BackendOrderDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  // ¿El negocio permite "Aceptar sin pago" (cobrar al recoger)? Gate del tenant
  // (admin › Pagos). Sólo aplica a pedidos TAKEOUT. Best-effort: si no carga, el
  // botón simplemente no aparece (el flujo normal de aceptar sigue igual).
  const [allowNoPay, setAllowNoPay] = useState(false)

  const reload = useCallback(async () => {
    const tid = getTenantId()
    if (!tid) { setError('No hay tenant activo.'); setLoading(false); return }
    try {
      setError(null)
      const [items, withRounds] = await Promise.all([
        listPendingAcceptanceOrders(tid),
        listOrdersWithPendingRounds(tid).catch(() => [] as BackendOrderDto[]),
      ])
      setOrders(items.slice().sort((a, b) => a.openedAt.localeCompare(b.openedAt)))
      setRoundOrders(withRounds.slice().sort((a, b) => a.openedAt.localeCompare(b.openedAt)))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar las órdenes por aceptar')
    } finally {
      setLoading(false)
    }
  }, [])

  /** Historial: pedidos recientes (todos los estatus) para revisar qué pasó con
   *  cada uno. El grafo `orders` saca el tenant del JWT; ordenamos por más nuevo.
   *  Sin polling — es una consulta bajo demanda (el botón "Recargar" la repite). */
  const loadHistory = useCallback(async () => {
    const tid = getTenantId()
    if (!tid) { setError('No hay tenant activo.'); return }
    setHistoryLoading(true)
    try {
      setError(null)
      const res = await listOrders({ tenantId: tid, limit: 50 })
      setHistory(res.items.slice().sort((a, b) => b.openedAt.localeCompare(a.openedAt)))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar el historial')
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    // Solo la bandeja "por aceptar" hace polling; el historial se carga una vez
    // al entrar (y con el botón Recargar). tenantId en deps: Test↔Live refresca.
    if (view === 'pending') {
      setLoading(true)
      void reload()
      const iv = setInterval(() => void reload(), POLL_MS)
      return () => clearInterval(iv)
    }
    void loadHistory()
    return undefined
  }, [tenantId, view, reload, loadHistory])

  useEffect(() => {
    // Gate del tenant para "Aceptar sin pago" (best-effort). tenantId en deps:
    // Test↔Live puede tener config distinta.
    let alive = true
    getTenantPaymentMethods()
      .then((g) => { if (alive) setAllowNoPay(g.allowAcceptWithoutPayment === true) })
      .catch(() => { if (alive) setAllowNoPay(false) })
    return () => { alive = false }
  }, [tenantId])

  const actor = user?.id ?? GUEST_SENTINEL

  /** Aceptar → OPEN + ventana de gracia. Si el gate de pago falla, el backend
   *  responde error visible → lo mostramos en la card (no se traga). */
  async function accept(o: BackendOrderDto, fireWithoutPayment = false) {
    setBusyId(o.id)
    setCardError((m) => { const n = { ...m }; delete n[o.id]; return n })
    try {
      if (fireWithoutPayment) {
        // "Aceptar sin pago" (TAKEOUT): el backend acepta Y dispara a cocina
        // dejando la orden OPEN/impaga (pendiente de cobro). Se cobra al recoger
        // en /para-llevar → "Cobrar para entregar". No firamos aquí (ya lo hace el
        // backend con el flag).
        await acceptOrder(o.id, actor, undefined, true)
      } else {
        await acceptOrder(o.id, actor)
        // DINE_IN: autorizar = comandar a cocina. Antes `acceptOrder` sólo dejaba la
        // orden OPEN + ventana de gracia (fireHoldUntil), pero NADA disparaba a cocina
        // al expirar (no hay scheduler), así que la orden quedaba atada a la mesa sin
        // llegar al KDS ("autorizada pero no sale para preparar"). TAKEOUT-efectivo NO
        // se dispara aquí: cobra primero en /para-llevar y ese flujo la comanda.
        if (o.type === 'DINE_IN') await fireOrder(o.id)
      }
      setAcceptedCount((n) => n + 1)
      await reload()
    } catch (e) {
      setCardError((m) => ({ ...m, [o.id]: e instanceof Error ? e.message : 'No se pudo aceptar la orden' }))
    } finally {
      setBusyId(null)
    }
  }

  function startReject(o: BackendOrderDto) {
    setRejectingId(o.id)
    setRejectReason('')
    setCardError((m) => { const n = { ...m }; delete n[o.id]; return n })
  }
  function cancelReject() { setRejectingId(null); setRejectReason('') }

  async function confirmReject(o: BackendOrderDto) {
    const reason = rejectReason.trim()
    if (!reason) { setCardError((m) => ({ ...m, [o.id]: 'Escribe el motivo del rechazo.' })); return }
    setBusyId(o.id)
    try {
      await rejectOrder(o.id, actor, reason)
      setRejectingId(null); setRejectReason('')
      await reload()
    } catch (e) {
      setCardError((m) => ({ ...m, [o.id]: e instanceof Error ? e.message : 'No se pudo rechazar la orden' }))
    } finally {
      setBusyId(null)
    }
  }

  /** Aceptar una RONDA (tanda que el comensal sometió sobre su cuenta) → la
   *  dispara a cocina (nace su ticket). key = roundId para no chocar con el
   *  busyId de las órdenes. */
  async function acceptRoundAction(o: BackendOrderDto, roundId: string) {
    const tid = getTenantId(); if (!tid) return
    setBusyId(roundId)
    setCardError((m) => { const n = { ...m }; delete n[roundId]; return n })
    try {
      await acceptRound(roundId, tid, actor)
      await reload()
    } catch (e) {
      setCardError((m) => ({ ...m, [roundId]: e instanceof Error ? e.message : 'No se pudo aceptar la ronda' }))
    } finally { setBusyId(null) }
  }
  async function rejectRoundAction(o: BackendOrderDto, roundId: string) {
    const tid = getTenantId(); if (!tid) return
    const reason = (roundRejectReason[roundId] ?? '').trim()
    if (!reason) { setCardError((m) => ({ ...m, [roundId]: 'Escribe el motivo del rechazo.' })); return }
    setBusyId(roundId)
    try {
      await rejectRound(roundId, tid, actor, reason)
      setRoundRejectingId(null)
      setRoundRejectReason((m) => { const n = { ...m }; delete n[roundId]; return n })
      await reload()
    } catch (e) {
      setCardError((m) => ({ ...m, [roundId]: e instanceof Error ? e.message : 'No se pudo rechazar la ronda' }))
    } finally { setBusyId(null) }
  }

  const stats = useMemo(() => {
    const pending = orders.length
    const longestWaitMin = orders.reduce((m, o) => Math.max(m, elapsedMin(o.openedAt)), 0)
    const totalCents = orders.reduce((s, o) => s + o.totalCents, 0)
    return { pending, longestWaitMin, totalCents }
  }, [orders])

  return (
    <div className="dhk8-acc-root">
      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <header className="dhk8-acc-hero">
        <div className="dhk8-acc-hero__top">
          <div>
            <div className="dhk8-acc-eyebrow">Punto de venta · Comensal</div>
            <h1 className="dhk8-acc-hero__title">Órdenes por aceptar</h1>
          </div>
          <Inbox size={22} className="dhk8-acc-hero__icon" aria-hidden />
        </div>
        <div className="dhk8-acc-hero__big">
          <div className="dhk8-acc-hero__number" aria-live="polite">{stats.pending}</div>
          <div className="dhk8-acc-hero__label">
            {stats.pending === 1 ? 'orden esperando autorización' : 'órdenes esperando autorización'}
          </div>
        </div>
        {stats.pending > 0 && (
          <div className="dhk8-acc-hero__stats">
            <div className="dhk8-acc-stat">
              <Clock size={14} aria-hidden />
              <span className="dhk8-acc-stat__num">{stats.longestWaitMin === 0 ? '<1' : stats.longestWaitMin}</span>
              <span className="dhk8-acc-stat__unit">min · espera más larga</span>
            </div>
            <div className="dhk8-acc-stat">
              <span className="dhk8-acc-stat__num">{formatCents(stats.totalCents)}</span>
              <span className="dhk8-acc-stat__unit">en órdenes pendientes</span>
            </div>
          </div>
        )}
      </header>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="dhk8-acc-toolbar">
        <div className="dhk8-acc-seg" role="tablist" aria-label="Vista de pedidos">
          <button type="button" role="tab" aria-selected={view === 'pending'}
            className={`dhk8-acc-seg__btn ${view === 'pending' ? 'is-on' : ''}`}
            onClick={() => setView('pending')}>
            Por aceptar
          </button>
          <button type="button" role="tab" aria-selected={view === 'history'}
            className={`dhk8-acc-seg__btn ${view === 'history' ? 'is-on' : ''}`}
            onClick={() => setView('history')}>
            Historial
          </button>
        </div>
        <button type="button" className="dhk8-acc-refresh"
          onClick={() => (view === 'pending' ? void reload() : void loadHistory())} title="Recargar">
          <RefreshCw size={14} /> Recargar
        </button>
      </div>
      {view === 'pending' && (
        <span className="dhk8-acc-hint">
          {acceptedCount > 0
            ? `${acceptedCount} orden(es) aceptada(s) en esta sesión`
            : 'Revisa el pedido y acepta para enviarlo a cocina (o recházalo con motivo)'}
        </span>
      )}

      {error && <div className="dhk8-acc-err" role="alert">{error}</div>}

      {view === 'history' ? (
        <>
          {historyLoading && <div className="dhk8-acc-empty">Cargando historial…</div>}
          {!historyLoading && history.length === 0 && !error && (
            <div className="dhk8-acc-empty">Sin pedidos en el historial reciente.</div>
          )}
          <ol className="dhk8-acc-list" aria-label="Historial de pedidos">
            {history.map((o) => {
              const sm = statusMeta(o.status)
              return (
                <li key={o.id} className="dhk8-acc-card dhk8-acc-card--hist">
                  <div className="dhk8-acc-card__rail" aria-hidden="true" />
                  <div className="dhk8-acc-card__body">
                    <div className="dhk8-acc-card__head">
                      <div className="dhk8-acc-card__head-left">
                        <strong className="dhk8-acc-card__folio">{orderRef(o)}</strong>
                        <span className="dhk8-acc-type">{typeLabel(o.type)}</span>
                      </div>
                      <span className={`dhk8-acc-badge dhk8-acc-badge--${sm.cls}`}>{sm.label}</span>
                    </div>
                    <ul className="dhk8-acc-items">
                      {o.items.filter((it) => it.status !== 'CANCELLED').map((it) => (
                        <li key={it.id} className="dhk8-acc-item">
                          <span className="dhk8-acc-item__qty">{it.quantity}×</span>
                          <span className="dhk8-acc-item__name">{it.name}</span>
                          <span className="dhk8-acc-item__price">{formatCents(it.totalCents)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="dhk8-acc-card__foot">
                      <div className="dhk8-acc-total">
                        <span className="dhk8-acc-total__label">Total</span>
                        <strong className="dhk8-acc-total__amount">{formatCents(o.totalCents)}</strong>
                      </div>
                      <span className="dhk8-acc-wait">
                        <Clock size={12} aria-hidden /> {fmtElapsed(o.openedAt)}
                      </span>
                    </div>
                  </div>
                </li>
              )
            })}
          </ol>
        </>
      ) : (
      <>
      {loading && <div className="dhk8-acc-empty">Cargando la bandeja…</div>}
      {!loading && orders.length === 0 && roundOrders.length === 0 && !error && (
        <div className="dhk8-acc-empty dhk8-acc-empty--zen">
          <span className="dhk8-acc-empty__icon" aria-hidden>🍃</span>
          <span>Nada por aceptar. Las órdenes del comensal aparecerán aquí.</span>
        </div>
      )}

      {/* ── RONDAS por aceptar (tandas que el comensal sumó a una cuenta ya
             abierta; cada una es un ticket de cocina aparte al aceptarla) ──── */}
      {roundOrders.length > 0 && (
        <ol className="dhk8-acc-list" aria-label="Rondas por aceptar">
          {roundOrders.flatMap((o) =>
            (o.pendingRounds ?? []).map((rnd) => {
              const busy = busyId === rnd.id
              const rejecting = roundRejectingId === rnd.id
              const roundItems = o.items.filter((it) => it.roundId === rnd.id && it.status !== 'CANCELLED')
              const total = roundItems.reduce((s, it) => s + it.totalCents, 0)
              return (
                <li key={rnd.id} className="dhk8-acc-card dhk8-acc-card--fresh">
                  <div className="dhk8-acc-card__rail" aria-hidden="true" />
                  <div className="dhk8-acc-card__body">
                    <div className="dhk8-acc-card__head">
                      <div className="dhk8-acc-card__head-left">
                        <strong className="dhk8-acc-card__folio">{orderRef(o)} · Ronda {rnd.number}</strong>
                        <span className="dhk8-acc-method">{rnd.origin === 'guest' ? 'Pedido del comensal' : 'Nueva tanda'}</span>
                      </div>
                      <span className="dhk8-acc-wait">
                        <Clock size={12} aria-hidden /> {fmtElapsed(rnd.createdAt)}
                      </span>
                    </div>
                    <ul className="dhk8-acc-items">
                      {roundItems.map((it) => (
                        <li key={it.id} className="dhk8-acc-item">
                          <span className="dhk8-acc-item__qty">{it.quantity}×</span>
                          <span className="dhk8-acc-item__name">
                            {it.name}
                            {it.modifiers.length > 0 && (
                              <span className="dhk8-acc-item__mods"> · {it.modifiers.map((mm) => mm.name).join(', ')}</span>
                            )}
                            {it.notes && <span className="dhk8-acc-item__note"> · {it.notes}</span>}
                          </span>
                          <span className="dhk8-acc-item__price">{formatCents(it.totalCents)}</span>
                        </li>
                      ))}
                    </ul>
                    {cardError[rnd.id] && <div className="dhk8-acc-carderr" role="alert">{cardError[rnd.id]}</div>}
                    <div className="dhk8-acc-card__foot">
                      <div className="dhk8-acc-total">
                        <span className="dhk8-acc-total__label">Esta ronda</span>
                        <strong className="dhk8-acc-total__amount">{formatCents(total)}</strong>
                      </div>
                      {!rejecting ? (
                        <div className="dhk8-acc-card__actions">
                          <button type="button" className="dhk8-acc-secondary dhk8-acc-secondary--danger"
                            disabled={busy} onClick={() => { setRoundRejectingId(rnd.id); setRoundRejectReason((m) => ({ ...m, [rnd.id]: '' })) }}
                            title="Rechazar esta ronda (requiere motivo)">
                            <X size={14} /> Rechazar
                          </button>
                          <button type="button" className="dhk8-acc-accept"
                            disabled={busy} onClick={() => void acceptRoundAction(o, rnd.id)}
                            title="Aceptar y enviar esta ronda a cocina">
                            {busy ? <>Aceptando…</> : <><Check size={15} /> Aceptar ronda</>}
                          </button>
                        </div>
                      ) : (
                        <div className="dhk8-acc-reject">
                          <input className="dhk8-acc-reject__input" autoFocus placeholder="Motivo del rechazo…"
                            value={roundRejectReason[rnd.id] ?? ''}
                            onChange={(e) => setRoundRejectReason((m) => ({ ...m, [rnd.id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') void rejectRoundAction(o, rnd.id); if (e.key === 'Escape') setRoundRejectingId(null) }} />
                          <button type="button" className="dhk8-acc-secondary" disabled={busy} onClick={() => setRoundRejectingId(null)}>Cancelar</button>
                          <button type="button" className="dhk8-acc-secondary dhk8-acc-secondary--danger"
                            disabled={busy} onClick={() => void rejectRoundAction(o, rnd.id)}>
                            {busy ? 'Rechazando…' : 'Confirmar rechazo'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )
            }),
          )}
        </ol>
      )}

      {/* ── LISTA ───────────────────────────────────────────────────────── */}
      <ol className="dhk8-acc-list" aria-label="Órdenes por aceptar">
        {orders.map((o) => {
          const mins = elapsedMin(o.openedAt)
          const tier = waitTier(mins)
          const m = methodMeta(o.chosenPaymentMethod)
          const isRejecting = rejectingId === o.id
          const busy = busyId === o.id
          return (
            <li key={o.id} className={`dhk8-acc-card dhk8-acc-card--${tier}`}>
              <div className="dhk8-acc-card__rail" aria-hidden="true" />
              <div className="dhk8-acc-card__body">
                <div className="dhk8-acc-card__head">
                  <div className="dhk8-acc-card__head-left">
                    <strong className="dhk8-acc-card__folio">{orderRef(o)}</strong>
                    <span className={`dhk8-acc-method ${m.known ? '' : 'dhk8-acc-method--none'}`}>
                      <m.Icon size={12} aria-hidden /> {m.label}
                    </span>
                    {/* Ya pagado (p. ej. pago de demostración del modo estadio): al
                        aceptar va directo a cocina. Sólo aparece si el pedido trae
                        saldo cubierto; los demás se ven igual que siempre. */}
                    {o.totalCents > 0 && (o.paidCents ?? 0) >= o.totalCents && (
                      <span className="dhk8-acc-method dhk8-acc-method--paid">
                        <Check size={12} aria-hidden /> Pagado
                      </span>
                    )}
                  </div>
                  <span className={`dhk8-acc-wait dhk8-acc-wait--${tier}`}>
                    <Clock size={12} aria-hidden /> {fmtElapsed(o.openedAt)}
                  </span>
                </div>

                <ul className="dhk8-acc-items">
                  {o.items.filter((it) => it.status !== 'CANCELLED').map((it) => (
                    <li key={it.id} className="dhk8-acc-item">
                      <span className="dhk8-acc-item__qty">{it.quantity}×</span>
                      <span className="dhk8-acc-item__name">
                        {it.name}
                        {it.modifiers.length > 0 && (
                          <span className="dhk8-acc-item__mods"> · {it.modifiers.map((mm) => mm.name).join(', ')}</span>
                        )}
                        {it.notes && <span className="dhk8-acc-item__note"> · {it.notes}</span>}
                      </span>
                      <span className="dhk8-acc-item__price">{formatCents(it.totalCents)}</span>
                    </li>
                  ))}
                </ul>

                {cardError[o.id] && <div className="dhk8-acc-carderr" role="alert">{cardError[o.id]}</div>}

                <div className="dhk8-acc-card__foot">
                  <div className="dhk8-acc-total">
                    {/* Las lineas son NETAS (subtotal = suma de item.totalCents). Sin este
                        desglose el Total parecia no cuadrar con las lineas de arriba. */}
                    {o.taxCents > 0 && (
                      <span className="dhk8-acc-total__break">
                        Subtotal {formatCents(o.subtotalCents)} · IVA {formatCents(o.taxCents)}
                      </span>
                    )}
                    <span className="dhk8-acc-total__label">Total</span>
                    <strong className="dhk8-acc-total__amount">{formatCents(o.totalCents)}</strong>
                  </div>
                  {!isRejecting ? (
                    <div className="dhk8-acc-card__actions">
                      <button type="button" className="dhk8-acc-secondary dhk8-acc-secondary--danger"
                        disabled={busy} onClick={() => startReject(o)} title="Rechazar la orden (requiere motivo)">
                        <X size={14} /> Rechazar
                      </button>
                      {allowNoPay && o.type === 'TAKEOUT' && (
                        <button type="button" className="dhk8-acc-secondary"
                          disabled={busy} onClick={() => void accept(o, true)}
                          title="Aceptar y enviar a cocina SIN cobrar; se cobra al recoger">
                          {busy ? <>Enviando…</> : <>Aceptar sin pago</>}
                        </button>
                      )}
                      <button type="button" className="dhk8-acc-accept"
                        disabled={busy} onClick={() => void accept(o)}
                        title="Aceptar y abrir la ventana de gracia antes de comandar">
                        {busy ? <>Aceptando…</> : <><Check size={15} /> Aceptar</>}
                      </button>
                    </div>
                  ) : (
                    <div className="dhk8-acc-reject">
                      <input className="dhk8-acc-reject__input" autoFocus placeholder="Motivo del rechazo…"
                        value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void confirmReject(o); if (e.key === 'Escape') cancelReject() }} />
                      <button type="button" className="dhk8-acc-secondary" disabled={busy} onClick={cancelReject}>Cancelar</button>
                      <button type="button" className="dhk8-acc-secondary dhk8-acc-secondary--danger"
                        disabled={busy} onClick={() => void confirmReject(o)}>
                        {busy ? 'Rechazando…' : 'Confirmar rechazo'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ol>
      </>
      )}

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  /* NintaiGO: sin border-radius, sin box-shadow. Elevación = borde 1px o inversión bg/fg. */
  .dhk8-acc-root { display: flex; flex-direction: column; gap: var(--pos-space-4, 16px); padding: var(--pos-space-4, 16px); max-width: 880px; }

  /* flex-shrink:0 en el hero, la toolbar y las tarjetas: son hijos de un flex
     column; sin esto se COMPRIMEN para caber en el alto disponible en vez de
     desbordar, y el hero (con el conteo grande) se apachurra cuando hay muchas
     órdenes. Mismo fix que el KDS. */
  .dhk8-acc-hero {
    position: relative; overflow: hidden; flex-shrink: 0;
    background: linear-gradient(135deg, color-mix(in oklch, var(--pos-tint, #1F8B3A) 16%, var(--pos-elevated, #1E2520)) 0%, var(--pos-surface, #161C18) 100%);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-left: 4px solid var(--pos-tint, #1F8B3A);
    padding: var(--pos-space-5, 20px) var(--pos-space-5, 20px) var(--pos-space-4, 16px);
    display: flex; flex-direction: column; gap: var(--pos-space-3, 12px);
  }
  .dhk8-acc-hero__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .dhk8-acc-eyebrow { font-size: var(--pos-text-xxs, 11px); letter-spacing: .08em; text-transform: uppercase; color: var(--pos-fg-tertiary, #7E8C82); font-weight: var(--pos-weight-semibold, 600); }
  .dhk8-acc-hero__title { margin: 4px 0 0; font-size: var(--pos-text-xl, 20px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, #E8EFE9); font-family: var(--font-display, system-ui); }
  .dhk8-acc-hero__icon { color: var(--pos-tint, #1F8B3A); flex-shrink: 0; }
  .dhk8-acc-hero__big { display: flex; align-items: baseline; gap: var(--pos-space-3, 12px); }
  .dhk8-acc-hero__number { font-family: var(--font-display, system-ui); font-size: clamp(56px, 9vw, 96px); line-height: 1; font-weight: 800; color: var(--pos-fg, #E8EFE9); letter-spacing: -.02em; font-variant-numeric: tabular-nums; }
  .dhk8-acc-hero__label { font-size: var(--pos-text-lg, 17px); color: var(--pos-fg-secondary, #B6BFB8); font-weight: var(--pos-weight-medium, 500); }
  .dhk8-acc-hero__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--pos-space-3, 12px); padding-top: var(--pos-space-3, 12px); border-top: 1px dashed var(--pos-separator, rgba(255,255,255,.10)); }
  .dhk8-acc-stat { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-acc-stat svg { color: var(--pos-fg-tertiary, #7E8C82); align-self: center; }
  .dhk8-acc-stat__num { font-family: var(--font-display, system-ui); font-weight: 700; font-size: var(--pos-text-xl, 20px); color: var(--pos-fg, #E8EFE9); font-variant-numeric: tabular-nums; }
  .dhk8-acc-stat__unit { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82); text-transform: lowercase; }

  .dhk8-acc-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-shrink: 0; }
  .dhk8-acc-hint { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-acc-refresh { background: transparent; color: var(--pos-fg-secondary, #B6BFB8); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); padding: 7px 12px; font: inherit; font-size: var(--pos-text-xs, 12px); cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
  .dhk8-acc-refresh:hover { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-tint, #1F8B3A); }

  /* Toggle segmentado Por aceptar / Historial. Sin radius (regla NintaiGO). */
  .dhk8-acc-seg { display: inline-flex; border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); }
  .dhk8-acc-seg__btn { background: transparent; color: var(--pos-fg-tertiary, #7E8C82); border: none; border-right: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); padding: 8px 14px; font: inherit; font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600); cursor: pointer; min-height: var(--pos-touch-target, 44px); }
  .dhk8-acc-seg__btn:last-child { border-right: none; }
  .dhk8-acc-seg__btn:hover { color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-acc-seg__btn.is-on { background: var(--pos-tint, #1F8B3A); color: var(--pos-on-tint, #fff); }

  /* Vista Historial: card de solo lectura + badge de estatus. */
  .dhk8-acc-card--hist .dhk8-acc-card__rail { background: var(--pos-separator-strong, rgba(255,255,255,.20)); }
  .dhk8-acc-type { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82); font-weight: var(--pos-weight-medium, 500); }
  .dhk8-acc-badge { display: inline-flex; align-items: center; font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600); padding: 3px 8px; border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-acc-badge--open   { color: var(--pos-tint-on, #48C76C); border-color: color-mix(in oklch, var(--pos-tint, #1F8B3A) 40%, transparent); background: var(--pos-tint-soft, rgba(31,139,58,.16)); }
  .dhk8-acc-badge--wait   { color: var(--pos-warning, #E0A23C); border-color: color-mix(in oklch, var(--pos-warning, #E0A23C) 40%, transparent); background: var(--pos-warning-bg, rgba(224,162,60,.14)); }
  .dhk8-acc-badge--done   { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-separator-strong, rgba(255,255,255,.20)); }
  .dhk8-acc-badge--danger { color: var(--pos-danger, #E5484D); border-color: color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); background: var(--pos-danger-bg, rgba(229,72,77,.10)); }
  .dhk8-acc-badge--muted  { color: var(--pos-fg-tertiary, #7E8C82); }

  .dhk8-acc-err { color: var(--pos-danger, #E5484D); font-size: var(--pos-text-sm, 13px); padding: 10px 12px; border: 1px solid color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent); background: var(--pos-danger-bg, rgba(229,72,77,.10)); }
  .dhk8-acc-empty { color: var(--pos-fg-tertiary, #7E8C82); font-size: var(--pos-text-sm, 13px); padding: 36px 16px; text-align: center; font-style: italic; }
  .dhk8-acc-empty--zen { display: flex; flex-direction: column; align-items: center; gap: var(--pos-space-2, 8px); background: var(--pos-surface, #161C18); border: 1px dashed var(--pos-separator-strong, rgba(255,255,255,.14)); padding: 48px 16px; }
  .dhk8-acc-empty__icon { font-size: 28px; }

  .dhk8-acc-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--pos-space-3, 12px); }
  .dhk8-acc-card { display: grid; grid-template-columns: 6px 1fr; gap: 0; background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); overflow: hidden; flex-shrink: 0; }
  .dhk8-acc-card__rail { width: 6px; }
  .dhk8-acc-card--fresh .dhk8-acc-card__rail { background: var(--pos-tint, #1F8B3A); }
  .dhk8-acc-card--wait  .dhk8-acc-card__rail { background: var(--pos-warning, #E0A23C); }
  .dhk8-acc-card--long  .dhk8-acc-card__rail { background: var(--pos-danger, #E5484D); }
  .dhk8-acc-card__body { padding: var(--pos-space-3, 12px) var(--pos-space-4, 16px); display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); min-width: 0; }

  .dhk8-acc-card__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
  .dhk8-acc-card__head-left { display: flex; align-items: center; gap: 10px; min-width: 0; flex-wrap: wrap; }
  .dhk8-acc-card__folio { font-family: var(--font-mono, ui-monospace); font-size: var(--pos-text-md, 15px); color: var(--pos-fg, #E8EFE9); font-weight: var(--pos-weight-semibold, 600); }
  .dhk8-acc-method { display: inline-flex; align-items: center; gap: 4px; font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600); padding: 3px 8px; color: var(--pos-fg-secondary, #B6BFB8); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); }
  .dhk8-acc-method--paid { color: var(--pos-tint-on, #48C76C); border-color: color-mix(in oklch, var(--pos-tint, #1F8B3A) 45%, transparent); background: var(--pos-tint-soft, rgba(31,139,58,.14)); }
  .dhk8-acc-method--none { color: var(--pos-warning, #E0A23C); border-color: color-mix(in oklch, var(--pos-warning, #E0A23C) 40%, transparent); background: var(--pos-warning-bg, rgba(224,162,60,.14)); }
  .dhk8-acc-wait { display: inline-flex; align-items: center; gap: 4px; font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600); padding: 3px 8px; }
  .dhk8-acc-wait--fresh { color: var(--pos-tint-on, #48C76C); background: var(--pos-tint-soft, rgba(31,139,58,.16)); }
  .dhk8-acc-wait--wait  { color: var(--pos-warning, #E0A23C); background: var(--pos-warning-bg, rgba(224,162,60,.18)); }
  .dhk8-acc-wait--long  { color: var(--pos-danger, #E5484D); background: var(--pos-danger-bg, rgba(229,72,77,.18)); }

  .dhk8-acc-items { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .dhk8-acc-item { display: flex; align-items: baseline; gap: 8px; font-size: var(--pos-text-sm, 13px); color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-acc-item__qty { color: var(--pos-tint-on, #48C76C); font-weight: 700; flex-shrink: 0; font-variant-numeric: tabular-nums; }
  .dhk8-acc-item__name { flex: 1; min-width: 0; }
  .dhk8-acc-item__mods, .dhk8-acc-item__note { color: var(--pos-fg-tertiary, #7E8C82); font-size: var(--pos-text-xs, 12px); }
  .dhk8-acc-item__price { color: var(--pos-fg, #E8EFE9); flex-shrink: 0; font-variant-numeric: tabular-nums; }

  .dhk8-acc-carderr { color: var(--pos-danger, #E5484D); font-size: var(--pos-text-xs, 12px); padding: 7px 10px; border: 1px solid color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent); background: var(--pos-danger-bg, rgba(229,72,77,.10)); }

  .dhk8-acc-card__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; border-top: 1px dashed var(--pos-separator, rgba(255,255,255,.10)); padding-top: var(--pos-space-3, 12px); margin-top: 2px; }
  .dhk8-acc-total { display: flex; flex-direction: column; gap: 1px; }
  .dhk8-acc-total__break { font-size: var(--pos-text-xxs, 11px); color: var(--pos-fg-tertiary, #7E8C82); font-variant-numeric: tabular-nums; }
  .dhk8-acc-total__label { font-size: var(--pos-text-xxs, 11px); color: var(--pos-fg-tertiary, #7E8C82); text-transform: uppercase; letter-spacing: .04em; }
  .dhk8-acc-total__amount { font-size: var(--pos-text-xl, 20px); color: var(--pos-fg, #E8EFE9); font-variant-numeric: tabular-nums; font-weight: var(--pos-weight-semibold, 600); }

  .dhk8-acc-card__actions, .dhk8-acc-reject { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .dhk8-acc-reject__input { flex: 1; min-width: 160px; background: var(--pos-canvas, #0F1411); color: var(--pos-fg, #E8EFE9); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); padding: 10px 12px; font: inherit; font-size: var(--pos-text-sm, 13px); min-height: var(--pos-touch-target, 44px); }
  .dhk8-acc-reject__input:focus { outline: none; border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-acc-secondary { background: transparent; color: var(--pos-fg-secondary, #B6BFB8); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); padding: 10px 12px; font: inherit; font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-medium, 500); cursor: pointer; display: inline-flex; align-items: center; gap: 5px; min-height: var(--pos-touch-target, 44px); }
  .dhk8-acc-secondary:hover:not(:disabled) { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-acc-secondary:disabled { opacity: .55; cursor: not-allowed; }
  .dhk8-acc-secondary--danger { color: var(--pos-danger, #E5484D); border-color: color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent); }
  .dhk8-acc-secondary--danger:hover:not(:disabled) { border-color: var(--pos-danger, #E5484D); background: var(--pos-danger-bg, rgba(229,72,77,.10)); }
  .dhk8-acc-accept { background: var(--pos-tint, #1F8B3A); color: var(--pos-on-tint, #fff); border: 1px solid var(--pos-tint, #1F8B3A); padding: 10px 16px; font: inherit; font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-semibold, 600); cursor: pointer; display: inline-flex; align-items: center; gap: 5px; min-height: var(--pos-touch-target, 44px); }
  .dhk8-acc-accept:hover:not(:disabled) { filter: brightness(1.08); }
  .dhk8-acc-accept:active:not(:disabled) { filter: brightness(.92); }
  .dhk8-acc-accept:disabled { opacity: .55; cursor: not-allowed; }

  @media (max-width: 560px) {
    .dhk8-acc-card__foot { align-items: stretch; }
    .dhk8-acc-card__actions, .dhk8-acc-reject { width: 100%; }
    .dhk8-acc-accept, .dhk8-acc-card__actions .dhk8-acc-secondary { flex: 1; justify-content: center; }
  }
`
